import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { site } from "@/mocks/site";
import { categories } from "@/mocks/categories";
import { submitForm } from "@/utils/formSubmit";

export default function Footer() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    setSubmitting(true);
    const result = await submitForm(form);
    setSubmitting(false);
    if (result.ok) {
      setStatus("success");
      setFormError("");
      form.reset();
    } else {
      setStatus("error");
      setFormError(result.message);
    }
  };

  return (
    <footer className="bg-primary-950 text-background-100">
      <div className="mx-auto w-full px-4 py-14 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-500 font-heading text-lg text-foreground-950" aria-hidden="true">
                513
              </span>
              <span className="font-heading text-xl tracking-wide text-background-50">513吃瓜</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-primary-100/80">
              {site.slogan}。我们记录热梗的来龙去脉，整理表情包出处，收集二创二改与评论区神评，让每一次冲浪都有后续。
            </p>
            <div className="mt-5 flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-900 text-primary-200">
                <i className="ri-mail-line" aria-hidden="true"></i>
              </span>
              <div className="text-sm">
                <p className="text-primary-100/60">合作与投稿邮箱</p>
                <a href={`mailto:${site.email}`} rel="nofollow" className="text-background-50 hover:text-accent-400">
                  {site.email}
                </a>
              </div>
            </div>
          </div>

          <nav aria-label="页脚导航">
            <h2 className="font-heading text-base text-background-50">逛一逛</h2>
            <ul className="mt-4 space-y-2.5 text-sm">
              {site.nav.map((item) => (
                <li key={item.key}>
                  <Link to={item.path} className="text-primary-100/80 transition-colors hover:text-accent-400">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-label="内容分类">
            <h2 className="font-heading text-base text-background-50">热门分类</h2>
            <ul className="mt-4 space-y-2.5 text-sm">
              {categories.map((category) => (
                <li key={category.key}>
                  <Link
                    to={`/topics?category=${category.key}`}
                    className="text-primary-100/80 transition-colors hover:text-accent-400"
                  >
                    {category.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <h2 className="font-heading text-base text-background-50">热梗日报订阅</h2>
            <p className="mt-4 text-sm leading-relaxed text-primary-100/80">
              每周一封，只发值得追的梗和表情包，不发广告。
            </p>
            <form
              id="newsletter-form"
              data-readdy-form
              action="https://readdy.ai/api/form/daio2vbp14a1h3mr7800"
              method="post"
              onSubmit={handleSubmit}
              className="mt-4"
            >
              <div className="flex flex-col gap-2 sm:flex-row">
                <label htmlFor="newsletter-email" className="visually-hidden">
                  邮箱地址
                </label>
                <input
                  id="newsletter-email"
                  type="email"
                  name="email"
                  required
                  placeholder="你的邮箱地址"
                  autoComplete="email"
                  className="w-full rounded-md border border-primary-800 bg-primary-900 px-3 py-2.5 font-label text-sm text-background-50 placeholder:text-primary-200/50 focus:border-accent-500 focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 font-label text-sm text-foreground-950 transition-colors hover:bg-accent-400 disabled:opacity-60"
                >
                  {submitting ? "提交中…" : "订阅"}
                </button>
              </div>
              <input
                type="text"
                name="website_alt"
                className="field-guard-alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
              {status === "success" ? (
                <p className="mt-2 text-xs text-secondary-300" role="status">
                  订阅成功，下期热梗日报会发到你的邮箱。
                </p>
              ) : null}
              {status === "error" ? (
                <p className="mt-2 text-xs text-accent-300" role="alert">
                  {formError}
                </p>
              ) : null}
            </form>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-3 border-t border-primary-800 pt-6 text-xs text-primary-100/60 sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} 513吃瓜 · 追梗不迷路，吃瓜有后续</p>
          <p>站内内容均来自公开网络传播整理，仅用于分享交流。</p>
        </div>
      </div>
    </footer>
  );
}